import { useRef, useState } from 'react';
import { ProductImage } from '../../ui/ProductImage';
import { MediaView } from '../shared/MediaView';
import { founder, salon } from '../../data/content';
import { useSite } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { productGallery } from '../../core/rules/media';
import { placeholderImages } from '../../data/placeholderImages';
import type { Product, Treatment } from '../../core/types/catalog';
type Props = {
  product: Product;
  salonTreatment?: Treatment;
};

/**
 * Galerij op de productpagina: eerst de packshot (heel, op een rustig vlak), dan de extra beelden
 * uit het beheer, en tot slot altijd de salonfoto met waarom Sarah dit kiest.
 *
 * Telefoon: één beeld per scherm, vegen, met stipjes eronder. Computer: grote packshot en de rest
 * in een rustig raster eronder, zodat het koopblok ernaast mee kan scrollen.
 */
export function ProductGallery({
  product,
  salonTreatment
}: Props) {
  const {
    policy
  } = useSite();
  // De packshot staat hieronder apart (heel, niet uitgesneden); de rest zijn de extra beelden.
  const own = productGallery(product, shopLabels.productImage(product)).slice(1);
  // Zonder eigen extra beelden: een textuurbeeld (docs/BEELDEN.md, P2), zodat je ziet hoe het aanvoelt.
  const extra = own.length > 0 ? own : [{
    image: placeholderImages.productTexture,
    alt: `Sfeerbeeld: textuur van crème en serum`
  }];
  const slides = 1 + extra.length + 1;
  const stripRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const onScroll = () => {
    const el = stripRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth * 0.88));
    if (i !== current) setCurrent(Math.min(slides - 1, Math.max(0, i)));
  };
  return <div className="min-w-0" data-id="element-8099" data-mp="92mafgv5etyc">
      <div ref={stripRef} onScroll={onScroll} tabIndex={0} role="region" aria-label={`Foto's van ${product.name}`} className="-mx-6 flex snap-x snap-mandatory gap-3 overflow-x-auto px-6 [scrollbar-width:none] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay lg:mx-0 lg:grid lg:grid-cols-2 lg:gap-4 lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden" data-id="element-8100" data-mp="91kvh1b0hnbc">
        {/* De packshot: het grootst, en het eerste wat je ziet. */}
        <div className="aspect-[4/5] w-[88%] shrink-0 snap-center bg-shell lg:col-span-2 lg:aspect-[5/4] lg:w-full" data-id="element-8101" data-mp="q1xhfxrz1n0t">
          <ProductImage src={product.image} alt={shopLabels.productImage(product)} loading="eager" className="h-full w-full p-[9%] lg:p-[7%]" data-id="element-8102" data-mp="3ojt1j1uahve6" />
        </div>

        {extra.map((m, i) => <MediaView key={`${i}-${m.image}`} media={m} sizes="(min-width: 1024px) 28vw, 88vw" className="aspect-[4/5] w-[88%] shrink-0 snap-center lg:w-full" data-id="element-8103" data-mp="2cky452xjyks" data-mpi={i} />)}

        <figure className={`relative w-[88%] shrink-0 snap-center lg:w-full ${extra.length % 2 === 0 ? 'lg:col-span-2' : ''}`} data-id="element-8104" data-mp="3atpiejqpihsy">
          <MediaView media={{
          image: founder.withProducts,
          alt: `${founder.name} in de salon in ${salon.city}`,
          focus: {
            x: 50,
            y: 35
          }
        }} sizes="(min-width: 1024px) 55vw, 88vw" className={`aspect-[4/5] w-full ${extra.length % 2 === 0 ? 'lg:aspect-[16/10]' : ''}`} data-id="element-8105" data-mp="1rxtgeb7su8q3" />
          <figcaption className="absolute bottom-4 left-4 right-4 bg-shell/95 p-4 text-[14px] leading-relaxed text-muted backdrop-blur-sm sm:right-auto sm:max-w-xs" data-id="element-8106" data-mp="1dytq6z4n1lrt">
            {salonTreatment ? <>
                <span className="text-ink" data-id="element-8107" data-mp="147fm0e1qrxsc">In de salon</span> onderdeel van het thuisprotocol bij {salonTreatment.name}.
              </> : <>
                <span className="text-ink" data-id="element-8108" data-mp="vaf1iihsmju6">Geselecteerd door {founder.firstName}</span>, na {policy.yearsExperience} jaar testen in de salon.
              </>}
          </figcaption>
        </figure>
      </div>

      {/* Stipjes op telefoon: waar je bent in de reeks. Decoratief; de strip zelf is te bedienen. */}
      <div className="mt-4 flex justify-center gap-1.5 lg:hidden" aria-hidden="true" data-id="element-8109" data-mp="2lf0f7brh56su">
        {Array.from({
        length: slides
      }, (_, i) => <span key={i} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-soft ${i === current ? 'w-5 bg-ink' : 'w-1.5 bg-ink/25'}`} data-id="element-8110" data-mp="2bnnpapp8kqpg" />)}
      </div>
    </div>;
}